// Domain-specific permission tab wrappers.
// Each wrapper fetches the relevant tree (registers or categories)
// and passes it to the generic PermissionTab component.

import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { registersApi, buildRegisterTree } from '@/lib/api/registers'
import { categoriesApi, buildCategoryTree, type CategoryType } from '@/lib/api/categories'
import { queryKeys } from '@/lib/api/queryKeys'
import { PermissionTab, type TreeNode } from './PermissionTab'
import type { Grant } from '@/lib/api/usersRoles'

// --- Helpers to convert domain types to generic TreeNode ---

function registerToTreeNode(nodes: ReturnType<typeof buildRegisterTree>): TreeNode[] {
  return nodes.map((n) => ({
    id: n.id,
    parent_id: n.parent_id,
    path: n.path,
    depth: n.depth,
    name: n.name,
    has_children: n.has_children,
    children: registerToTreeNode(n.children),
  }))
}

function categoryToTreeNode(nodes: ReturnType<typeof buildCategoryTree>): TreeNode[] {
  return nodes.map((n) => ({
    id: n.id,
    parent_id: n.parent_id,
    path: n.path,
    depth: n.depth,
    name: n.name,
    has_children: n.has_children,
    children: categoryToTreeNode(n.children),
  }))
}

// --- Register permissions ---

export function RegisterPermissionTab({ roleId }: { roleId: string }) {
  const { slug } = useTenant()
  const api = useApi()
  const regApi = registersApi(api)

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.registers.tree(slug),
    queryFn: () => regApi.tree(),
  })

  const treeNodes = data ? registerToTreeNode(buildRegisterTree(data)) : []

  return (
    <PermissionTab
      roleId={roleId}
      domain="register"
      treeNodes={treeNodes}
      isTreeLoading={isLoading}
      treeError={error}
      onRefetchTree={() => refetch()}
    />
  )
}

// --- Category permission wrapper (reusable for control/event/action/framework) ---

function CategoryPermissionTab({
  roleId,
  categoryType,
  domain,
}: {
  roleId: string
  categoryType: CategoryType
  domain: Grant['domain']
}) {
  const { slug } = useTenant()
  const api = useApi()
  const catApi = categoriesApi(api, categoryType)

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.categories.tree(slug, categoryType),
    queryFn: () => catApi.tree(),
  })

  const treeNodes = data ? categoryToTreeNode(buildCategoryTree(data)) : []

  return (
    <PermissionTab
      roleId={roleId}
      domain={domain}
      treeNodes={treeNodes}
      isTreeLoading={isLoading}
      treeError={error}
      onRefetchTree={() => refetch()}
    />
  )
}

// --- Exported domain tabs ---

export function ControlPermissionTab({ roleId }: { roleId: string }) {
  return <CategoryPermissionTab roleId={roleId} categoryType="control" domain="control" />
}

export function EventPermissionTab({ roleId }: { roleId: string }) {
  return <CategoryPermissionTab roleId={roleId} categoryType="event" domain="event" />
}

export function ActionPermissionTab({ roleId }: { roleId: string }) {
  return <CategoryPermissionTab roleId={roleId} categoryType="action" domain="action" />
}

export function FrameworkPermissionTab({ roleId }: { roleId: string }) {
  return <CategoryPermissionTab roleId={roleId} categoryType="framework" domain="framework" />
}
